<script setup lang="ts">
  import { useAppStateProvider } from '@gat-ship/utils'
  import { IconClose } from '@gat-ship/icons'
  import { DialogClose, DialogContent, DialogPortal } from 'reka-ui'
  import { computed, useSlots, type HTMLAttributes } from 'vue'
  import { cn } from '../../lib/utils'
  import { useDialogZIndex } from '../../composables/useDialogZIndex'
  import { useModalStackEntry } from '../../lib/modal-stack'
  import { Sheet, SheetTrigger, SheetOverlay } from '../ui/sheet'
  import { testIds } from '@gat-ship/types'

  export interface ModalDrawerProps {
    class?: HTMLAttributes['class']
    // Consumed by useAppStateProvider(props, true) to register this drawer as a loading target; not referenced directly in the template.
    // eslint-disable-next-line vue/no-unused-properties
    loading?: boolean
    closeOnOutsideClick?: boolean
    hideCloseButton?: boolean
    maxWidth?: number
  }

  defineOptions({ inheritAttrs: false })

  const props = withDefaults(defineProps<ModalDrawerProps>(), {
    maxWidth: 400
  })

  const { isLoading } = useAppStateProvider(props, true)

  const isOpen = defineModel<boolean>('open', { default: false })

  const slots = useSlots()

  const { zIndex } = useDialogZIndex(isOpen)

  // When rendered as a central-host stack entry, render non-modal so the stack
  // owns interaction order (a raised buried drawer must become interactive).
  // Only the top entry shows a dim, pointer-blocking overlay. See
  // `useModalStackEntry`.
  const { isHostStackEntry, isTop } = useModalStackEntry()

  const contentStyle = computed(() => ({
    zIndex: zIndex.value,
    ...(props.maxWidth ? { maxWidth: `${props.maxWidth}px` } : {})
  }))

  const showCloseButton = computed(
    () => !isLoading.value && !props.hideCloseButton
  )

  function onOutsideClick(e: Event) {
    if (!props.closeOnOutsideClick) {
      e.preventDefault()
    }
  }
</script>
<template>
  <Sheet v-model:open="isOpen" :modal="!isHostStackEntry">
    <SheetTrigger v-if="slots['trigger']" as-child>
      <slot name="trigger" />
    </SheetTrigger>
    <DialogPortal>
      <!-- reka's SheetOverlay is modal-gated (renders nothing at modal=false),
           so host entries use a plain dim div instead. Gate it on `isOpen` (not
           just `isTop`): sibling detail modals can share one host slot, so a
           closed drawer can still read isTop=true — only the open top entry may
           paint a backdrop. Non-host keeps reka's SheetOverlay (Presence handles
           its enter/exit animation). -->
      <component
        :is="isHostStackEntry ? 'div' : SheetOverlay"
        v-if="!isHostStackEntry || (isOpen && isTop)"
        class="fixed inset-0 grid grid-cols-1 place-items-end bg-black/15 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        :style="{ zIndex }"
        @click.prevent
      />
      <DialogContent
        data-slot="sheet-content"
        :class="
          cn(
            'fixed inset-y-0 right-0 min-w-[320px] overflow-y-auto p-2 pl-4 transition ease-in-out data-[state=closed]:duration-100 data-[state=open]:duration-100 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            props.class
          )
        "
        v-bind="$attrs"
        :style="contentStyle"
        :aria-describedby="undefined"
        @interact-outside="onOutsideClick($event)"
        @escape-key-down="isOpen = false"
      >
        <div
          class="bg-background relative flex min-h-full w-full flex-col rounded-lg shadow-md"
        >
          <slot />
        </div>

        <DialogClose
          v-if="showCloseButton"
          class="ring-offset-background focus:ring-ring focus:outline-hidden bg-background shadow-xs absolute left-2 top-1 flex size-6 items-center justify-center rounded-full border transition-opacity hover:cursor-pointer hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
          :data-testid="testIds.common.dialog.closeX"
          @click="isOpen = false"
        >
          <icon-close class="size-3.5" />
          <span class="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </DialogPortal>
  </Sheet>
</template>
